import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Identity } from "@dfinity/agent";
import { initAuth, login, logout, type AuthState } from "../lib/auth";

type AuthContextValue = {
  identity: Identity | null;
  isAuthenticated: boolean;
  principalText: string;
  loading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    identity: null,
    isAuthenticated: false,
    principalText: "",
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initAuth().then((s) => {
      setState(s);
      setLoading(false);
    });
  }, []);

  async function handleLogin() {
    const s = await login();
    setState(s);
  }

  async function handleLogout() {
    await logout();
    setState({ identity: null, isAuthenticated: false, principalText: "" });
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        loading,
        login: handleLogin,
        logout: handleLogout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
